<div class="rescue-page">
    <img class="rescue-shield" src="../../../assets/images/rescuemii-shield.png" alt="RescueMII">
    <h1>RescueMII&trade; Privacy Policy</h1>
    <p class="text-center text-muted">Effective date: October 16, 2026</p>

    <!-- AC-4598: still open for legal: retention periods (after account deletion; location history; captures and
         recordings) and whether RescueMII gets its own terms instead of the domii license. -->

    <p>This policy explains what information the RescueMII&trade; mobile app (&ldquo;RescueMII&rdquo;, &ldquo;the app&rdquo;),
        collects, how we use and share it, and the choices you have. RescueMII is provided by IA9 Inc.
        (&ldquo;IA9&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). Our separate domii&reg; app for real estate professionals has its own
        privacy policy.</p>

    <h2>1. Information we collect</h2>

    <h3>Information you give us</h3>
    <ul>
        <li><strong>Account details:</strong> your first and last name, mobile number and email address. Your password is
            handled by our sign-in provider (Microsoft); we never see it.</li>
        <li><strong>Invitation:</strong> the invitation code you sign up with, which connects your account to the person who
            invited you.</li>
        <li><strong>Emergency contacts:</strong> the names and mobile numbers of up to five people you choose. Please only add
            people who have agreed to be contacted on your behalf.</li>
        <li><strong>Support messages:</strong> anything you send us when you contact support.</li>
    </ul>

    <h3>Information collected when you use the app</h3>
    <ul>
        <li><strong>Location:</strong> your precise location when you use RESCUE, HELLO, CHECKING-IN and the other safety
            features, so it can be shared with your emergency contacts. When you open the app or return to it, we also update
            your current location so your safety alerts and notifications are ready to go. If you allow it, the app uses your
            location in the background so your contacts can keep following your live location after RESCUE is activated, even
            when the app is closed or your phone is locked.</li>
        <li><strong>Photos, videos and audio:</strong> when you use CAPTURE or RECORD, the photos, videos and audio
            recordings you take are uploaded and delivered to your emergency contacts. We use your camera and microphone only
            when you use these features or take a profile picture.</li>
        <li><strong>Voice prompts:</strong> if you turn on voice prompts or use Siri (&ldquo;Hey Siri, Activate
            RescueMII&rdquo;), your phone&rsquo;s own speech recognition (provided by Apple or Google) listens for your chosen
            phrases. Recognition runs on your phone; RescueMII does not send your voice to IA9.</li>
        <li><strong>Device information:</strong> your device type, operating system, app version and a notification token so
            we can send you alerts.</li>
        <li><strong>Usage and crash data:</strong> how the app is used and diagnostic reports when it crashes, collected through
            Google Firebase Analytics and Crashlytics. We do not collect your device&rsquo;s advertising ID.</li>
        <li><strong>Purchases:</strong> if you buy a membership, the payment is processed by our payment provider, Braintree
            (a PayPal service). We receive confirmation of the purchase, not your full card number.</li>
    </ul>

    <h2>2. How we use your information</h2>
    <ul>
        <li>To provide the safety features: alerting your emergency contacts, sharing your live location and delivering your
            captures and recordings.</li>
        <li>To create and secure your account, and to connect it to the person who invited you.</li>
        <li>To send you notifications and text messages about your account and your safety alerts.</li>
        <li>To provide support, fix problems and improve the app.</li>
        <li>To process memberships and payments.</li>
        <li>To comply with the law and protect the rights and safety of our users and others.</li>
    </ul>

    <h2>3. How we share your information</h2>
    <ul>
        <li><strong>Your emergency contacts:</strong> when you use a safety feature, the contacts you chose receive your
            name, your location and any captures or recordings, by text message and a secure link.</li>
        <li><strong>The person who invited you:</strong> in their list of invitations they can see your name, the mobile
            number the invitation was sent to, and whether you have joined.</li>
        <li><strong>Service providers</strong> who run parts of RescueMII for us, under contract: Microsoft Azure (hosting,
            storage and sign-in), Twilio (text messages), Google Firebase (notifications, analytics and crash reports), Google
            Maps (maps) and Braintree (payments).</li>
        <li><strong>Emergency services:</strong> TAP 911 places an ordinary phone call from your device. We do not send your
            information to emergency services ourselves, unless the law requires it.</li>
        <li><strong>Legal reasons:</strong> when required by law, or to protect the safety of any person.</li>
        <li><strong>Business transfers:</strong> if IA9 is involved in a merger, acquisition or sale of assets, your
            information may be transferred as part of that transaction.</li>
    </ul>
    <p>We do not sell your personal information, and we do not use it to track you across other companies&rsquo; apps or
        websites.</p>

    <h2>4. How long we keep it</h2>
    <p>We keep your account information while your account is open, and we keep location data, captures and recordings
        only as long as we need them to provide RescueMII and its safety features. When you delete your account, we delete
        or de-identify your personal information, except where we must keep it longer by law.</p>

    <h2>5. Your choices</h2>
    <ul>
        <li><strong>Permissions:</strong> you can turn location, camera, microphone, notifications and Siri access on or off
            at any time in your phone&rsquo;s settings. Some safety features won&rsquo;t work without them.</li>
        <li><strong>Emergency contacts:</strong> you can change or remove them in the app.</li>
        <li><strong>Delete your account:</strong> in the app, go to My Account &rarr; My Settings &rarr; Delete Account, or
            email <a href="mailto:support@domii.io">support@domii.io</a> from the email
            address on your account. See our <a routerLink="/rescue/support" fragment="delete-account">support page</a>.</li>
        <li><strong>Access or correct your information:</strong> you can update most details in the app, or contact us.</li>
    </ul>

    <h2 id="california">6. Your California privacy rights</h2>
    <p>If you are a California resident, the California Consumer Privacy Act (as amended by the California Privacy Rights
        Act) gives you these rights:</p>
    <ul>
        <li><strong>Right to know:</strong> to ask what personal information we have collected about you, where it came from,
            why we use it and who we share it with, and to get a copy of it.</li>
        <li><strong>Right to delete:</strong> to ask us to delete the personal information we have collected from you,
            subject to exceptions allowed by law.</li>
        <li><strong>Right to correct:</strong> to ask us to correct inaccurate personal information.</li>
        <li><strong>Right to opt out of sale or sharing:</strong> we do not sell your personal information and do not share
            it for cross-context behavioral advertising.</li>
        <li><strong>Right to limit the use of sensitive personal information:</strong> we use sensitive personal
            information, such as your precise location and your recordings, only to provide RescueMII and its safety
            features.</li>
        <li><strong>Right to non-discrimination:</strong> we will not treat you differently for exercising these rights.</li>
    </ul>
    <p>In the last 12 months we have collected these categories of personal information, as described in section 1:
        identifiers and contact details (name, mobile number, email, device identifiers), your emergency contacts, precise
        geolocation, audio and visual information (captures and recordings), device and app usage information, and purchase
        records. We collect them from you and from your device, and use and share them only as described in sections 2
        and 3.</p>
    <p>To make a request, email <a href="mailto:support@domii.io">support@domii.io</a> or call
        <a href="tel:+18663664446">1-866-366-4446</a>. We will verify your request using the details on your account and
        respond within the time the law requires. You may use an authorized agent to make a request for you; we may ask
        for proof that the agent is authorized. California residents may also ask, once a year and free of charge, about
        any personal information we disclosed to third parties for their direct marketing purposes; we do not make such
        disclosures.</p>

    <h2>7. Security</h2>
    <p>We use encryption in transit and industry-standard safeguards to protect your information. No method of transmission
        or storage is completely secure, so we cannot guarantee absolute security.</p>

    <h2>8. Age requirement</h2>
    <p>RescueMII is designed for people 18 and older. It is not intended for anyone under 18, and we do not knowingly
        collect personal information from them. If you believe someone under 18 has given us information, contact us and
        we will delete it.</p>

    <h2>9. Changes to this policy</h2>
    <p>We may update this policy from time to time. We will post the new version on this page with a new effective date, and
        tell you in the app or by email if the changes are significant.</p>

    <h2>10. Contact us</h2>
    <p>IA9 Inc.<br>
        1013 Centre Road, Suite 403-A<br>
        Wilmington, DE 19805<br>
        <a href="mailto:support@domii.io">support@domii.io</a> &middot; <a href="tel:+18663664446">1-866-366-4446</a></p>
</div>
<app-footer></app-footer>
